import React from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { InputNumber, Select, Switch } from "antd";
// COMPONENTS
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import EnumHelpTable, { type EnumHelpRow } from "./EnumHelpTable";
// FLATBUFFER ENUMS
import { RedundancySupport } from "../../../../../flatbuffers/generated/opc-ua/redundancy-support";
// INTERFACES
import type { OpcUaRedundancyForm } from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

export const BLANK_REDUNDANCY: OpcUaRedundancyForm = {
  mode: RedundancySupport.None_,
  failoverTimeoutMs: 5000,
};

const REDUNDANCY_MODE_OPTIONS: { value: RedundancySupport; label: string }[] = [
  { value: RedundancySupport.None_, label: "None" },
  { value: RedundancySupport.Cold, label: "Cold" },
  { value: RedundancySupport.Warm, label: "Warm" },
  { value: RedundancySupport.Hot, label: "Hot" },
  { value: RedundancySupport.Transparent, label: "Transparent" },
  { value: RedundancySupport.HotAndMirrored, label: "Hot and Mirrored" },
];

/** Redundant-server failover configuration. Optional — a switch toggles between no redundancy (null) and an explicit config. */
export default function RedundancyEditor({
  redundancy,
  onChangeRedundancy,
}: {
  redundancy: OpcUaRedundancyForm | null;
  onChangeRedundancy: (redundancy: OpcUaRedundancyForm | null) => void;
}) {
  const [t] = useTranslation("global");

  const onToggleEnabled = (enabled: boolean) =>
    onChangeRedundancy(enabled ? { ...BLANK_REDUNDANCY } : null);
  const onChangeField = <K extends keyof OpcUaRedundancyForm>(
    field: K,
    value: OpcUaRedundancyForm[K],
  ) => {
    onChangeRedundancy({ ...(redundancy ?? BLANK_REDUNDANCY), [field]: value });
  };

  const redundancyModeHelpRows: EnumHelpRow[] = [
    { value: "None", meaning: t("general.opcUaRedundancyNoneDesc") },
    { value: "Cold", meaning: t("general.opcUaRedundancyColdDesc") },
    { value: "Warm", meaning: t("general.opcUaRedundancyWarmDesc") },
    { value: "Hot", meaning: t("general.opcUaRedundancyHotDesc") },
    { value: "Transparent", meaning: t("general.opcUaRedundancyTransparentDesc") },
    { value: "Hot and Mirrored", meaning: t("general.opcUaRedundancyHotAndMirroredDesc") },
  ];

  return (
    <div className="container mt-3" style={{ width: "100%", maxWidth: "none" }}>
      <div style={{ marginBottom: 16 }}>
        <OpcUaFieldLabel label="Configure redundancy" />
        <div style={{ marginTop: 8 }}>
          <Switch checked={redundancy !== null} onChange={onToggleEnabled} />
        </div>
      </div>
      {redundancy && (
        <div className="row">
          <div className="col-12 col-md-6 mt-2">
            <OpcUaFieldLabel
              label="Redundancy Mode"
              tooltip={
                <EnumHelpTable
                  valueHeader={t("general.opcUaEnumTableValue")}
                  meaningHeader={t("general.opcUaEnumTableMeaning")}
                  rows={redundancyModeHelpRows}
                />
              }
            />
            <Select
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              value={redundancy.mode}
              onChange={(v: RedundancySupport) => onChangeField("mode", v)}
            >
              {REDUNDANCY_MODE_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <div className="col-12 col-md-6 mt-2">
            <OpcUaFieldLabel
              label="Failover Timeout (ms)"
              tooltip={t("general.opcUaFailoverTimeoutHelp")}
            />
            <InputNumber
              className="generalStyles__width100"
              size="large"
              style={{ width: "100%", marginTop: 8 }}
              min={0}
              value={redundancy.failoverTimeoutMs}
              onChange={(v) => onChangeField("failoverTimeoutMs", v ?? 0)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
